import { useCallback } from 'react';
import { useNgInReactService } from '~/apps/main/App/Ng/NgInReactProvider';
import { useInjector } from '~/common/useInjector';
import { IModalService } from '~/types/angular.types';
import { isReactFirst } from '~/utils/environment.utils';

interface UseModalServiceOptions<TMethodName extends keyof IModalService> {
    modalServiceMethodName: TMethodName;
    iFrameStyle?: Partial<CSSStyleDeclaration>;
    onSuccess?: (result: ReturnType<IModalService[TMethodName]>) => void;
    onError?: (error: any) => void;
}

export const useModalService = <TMethodName extends keyof IModalService>({
    modalServiceMethodName,
    iFrameStyle,
    onSuccess,
    onError
}: UseModalServiceOptions<TMethodName>) => {
    const ngInReactService = useNgInReactService();
    const { ModalService } = useInjector(['ModalService']);

    return useCallback(
        async (overrideParams: Parameters<IModalService[TMethodName]>) => {
            if (isReactFirst()) {
                return ngInReactService
                    .openAngularModal({
                        modalServiceMethodName,
                        modalParams: overrideParams,
                        iFrameStyle
                    })
                    .then((result: ReturnType<IModalService[TMethodName]>) => {
                        onSuccess?.(result);
                        return result;
                    })
                    .catch((error: any) => {
                        onError?.(error);
                        throw error;
                    });
            }

            // For non-React First, directly call the modal service method
            const modalServiceMethod = (ModalService as any)[
                modalServiceMethodName
            ];
            if (typeof modalServiceMethod !== 'function') {
                throw new Error(
                    `Method ${modalServiceMethodName} not found on ModalService`
                );
            }

            try {
                const result = await modalServiceMethod.apply(
                    ModalService,
                    overrideParams
                );
                onSuccess?.(result);
                return await Promise.resolve(result);
            } catch (error) {
                onError?.(error);
                return Promise.reject(error);
            }
        },
        [
            modalServiceMethodName,
            onSuccess,
            onError,
            ModalService,
            ngInReactService,
            iFrameStyle
        ]
    );
};
